import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { API_URLS } from "../config";
import { getPostLoginPath } from "../utils/postLoginRedirect";
import { FiEye, FiEyeOff, FiMail, FiLock } from "react-icons/fi";
import { FaHubspot } from "react-icons/fa";

const Login = () => {
  const navigate  = useNavigate();
  const { login } = useAuth();
  const [credentials, setCredentials] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotMsg, setShowForgotMsg] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({ ...prev, [name]: value }));
    if (error) setError("");
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await fetch(API_URLS.LOGIN, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(credentials),
      });

      const data = await response.json();

      if (response.ok && data.otpRequired) {
        // Unrecognized IP — hand off to the OTP screen instead of logging in directly.
        navigate("/verify-otp", {
          state: { pendingToken: data.pendingToken, maskedEmail: data.maskedEmail },
        });
      } else if (response.ok) {
        await login(data.accessToken, data.refreshToken, data.user);
        navigate(getPostLoginPath());
      } else {
        setError(data.message || "Invalid email or password.");
      }
    } catch (err) {
      console.error("Login error:", err);
      setError("Network error. Please check your connection.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="Login">
      <div
        className="login-left-panel"
        style={{
          background: `linear-gradient(135deg, rgba(10,10,18,0.75) 0%, rgba(20,18,18,0.5) 100%), url('/login-bg.jpg') center/cover no-repeat`,
        }}
      >
        <div className="login-left-content">
          <h1>Inspection Operations &amp;<br /> Execution Network</h1>
          <p>Streamline your inspection operations with precision and efficiency.</p>
        </div>
      </div>

      <div className="login-right-panel">
        <div className="login-card">
          <div className="login-brand">
            <FaHubspot className="login-brand-icon" />
            <span className="login-brand-name">IOEN</span>
          </div>

          <div className="login-heading">
            <h2>Welcome Back</h2>
            <p>Please enter your details to sign in.</p>
          </div>

          <form className="login-form" onSubmit={handleLogin}>
            <div className="login-field">
              <label>Email</label>
              <div className="login-input-wrapper">
                <FiMail className="login-input-icon" />
                <input
                  name="email"
                  type="email"
                  placeholder="name@example.com"
                  value={credentials.email}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>
            </div>

            <div className="login-field">
              <label>Password</label>
              <div className="login-input-wrapper">
                <FiLock className="login-input-icon" />
                <input
                  name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={credentials.password}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="login-eye-btn"
                >
                  {showPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                </button>
              </div>
            </div>

            <div className="login-options">
              <button
                type="button"
                className="login-forgot"
                onClick={() => setShowForgotMsg(v => !v)}
              >
                Forgot Password?
              </button>
            </div>

            {showForgotMsg && (
              <div className="login-forgot-msg">
                To reset or change your password, please contact the IT team directly.
              </div>
            )}

            {error && (
              <div className="login-error">{error}</div>
            )}

            <button type="submit" className="Login-btn login-submit-btn" disabled={loading}>
              {loading ? "Signing in…" : "Sign In"}
            </button>
          </form>

          <p className="login-support">
            Having trouble logging in?{" "}
            <a href="mailto:itsupport@apsgrp.com" className="login-support-link">
              Contact Technical Support
            </a>
          </p>
        </div>
      </div>
    </section>
  );
};

export default Login;
